import {create} from 'zustand';
import globalStore from '@/services/global.state';
import {IUserInfo} from '@/services/global.service';
import {ShareInfo} from '@/components/business/share-panel/share-panel-provider';
import {
  getShareInfo,
  updateInviteCode,
} from '@/common-pages/hooks/share.service';
import {getUserList, getUserTotal} from '../invitation.service';
import {UserListItem, UserTotal} from '../invitation.type';
import {getAgentInfo} from '../agent.service';
import {AgentInfo} from '../agent.type';
import {errorLog, goTo, openShare} from '@/utils';
import i18n from '@/i18n';
import {makeShareUrl} from '@/common-pages/hooks/share-url';

const makeShareMessage = (msg: string) =>
  msg.replace(/http(.*)$/g, '').concat(makeShareUrl('/invitation-apply'));

interface InvitationHomeState {
  code: string;
  inviteText: string;
  loading: boolean;
  login: boolean;
  userInfo?: IUserInfo;
  agentInfo?: AgentInfo;
  userList?: UserListItem[];
  userTotal?: UserTotal;
  setLogin: (login: boolean) => void;
  hydrateUser: () => Promise<void>;
  loadInvitationData: () => Promise<void>;
  refreshCode: () => Promise<void>;
  doShare: (info?: ShareInfo) => Promise<void>;
}

let shareInfoRequest: ReturnType<typeof getShareInfo> | undefined;

const loadShareInfo = () => {
  if (!shareInfoRequest) {
    shareInfoRequest = getShareInfo().finally(() => {
      shareInfoRequest = undefined;
    });
  }
  return shareInfoRequest;
};

export const useInvitationHomeStore = create<InvitationHomeState>(
  (set, get) => ({
    code: '',
    inviteText: '',
    loading: false,
    login: Boolean(globalStore.token),
    userInfo: undefined,
    agentInfo: undefined,
    userList: undefined,
    userTotal: undefined,

    setLogin: login => {
      set({
        login,
        ...(login
          ? {}
          : {
              code: '',
              inviteText: '',
              userList: undefined,
              userTotal: undefined,
              userInfo: undefined,
              agentInfo: undefined,
            }),
      });
    },

    hydrateUser: async () => {
      if (!get().login) {
        set({userInfo: undefined});
        return;
      }

      try {
        const cachedUser = await globalStore.asyncGetItem('user');
        const globalUser = globalStore.userInfo as IUserInfo | null;

        if (!cachedUser) {
          set({userInfo: globalUser || undefined});
          return;
        }

        const userInfo = JSON.parse(cachedUser) as IUserInfo;
        if (!userInfo.userPhone && globalUser?.userPhone) {
          userInfo.userPhone = globalUser.userPhone;
        }

        set({userInfo});
      } catch (error) {
        errorLog('invitation hydrate user failed', error);
        set({
          userInfo: (globalStore.userInfo as IUserInfo | null) || undefined,
        });
      }
    },

    loadInvitationData: async () => {
      if (!get().login) {
        return;
      }

      set({loading: true});

      try {
        const [listResult, totalResult, shareResult, agentResult] =
          await Promise.allSettled([
            getUserList(),
            getUserTotal(),
            loadShareInfo(),
            getAgentInfo(),
          ]);

        if (listResult.status === 'fulfilled') {
          set({userList: listResult.value});
          globalStore.asyncSetItem(
            'invitationBonusList',
            JSON.stringify(listResult.value),
          );
        }

        if (totalResult.status === 'fulfilled' && totalResult.value != null) {
          set({userTotal: totalResult.value});
        }

        if (shareResult.status === 'fulfilled') {
          set({
            code: shareResult.value.inviteCode,
            inviteText: shareResult.value.inviteRegisterLinkUrl,
          });
        }

        if (agentResult.status === 'fulfilled' && agentResult.value != null) {
          set({agentInfo: agentResult.value});
        }
      } catch (error) {
        errorLog('invitation data load failed', error);
      } finally {
        set({loading: false});
      }
    },

    refreshCode: async () => {
      if (!get().login) {
        goTo('Login');
        return;
      }

      set({loading: true});

      try {
        await updateInviteCode();
        const shareInfo = await loadShareInfo();
        set({
          code: shareInfo.inviteCode,
          inviteText: shareInfo.inviteRegisterLinkUrl,
        });
      } catch (error) {
        errorLog('invitation code refresh failed', error);
      } finally {
        set({loading: false});
      }
    },

    doShare: async (info: ShareInfo = {}) => {
      if (!get().login) {
        goTo('Login');
        return;
      }

      let {code, inviteText} = get();
      if (!code || !inviteText) {
        try {
          const shareInfo = await loadShareInfo();
          code = shareInfo.inviteCode;
          inviteText = shareInfo.inviteRegisterLinkUrl;
          set({code, inviteText});
        } catch (error) {
          errorLog('invitation share data load failed', error);
          globalStore.globalWaringTotal(
            i18n.t('invitation.referral.shareUnavailable'),
          );
          return;
        }
      }
      const shareText = `${makeShareMessage(inviteText)}?code=${code}`;

      if (openShare(shareText)) {
        return;
      }

      globalStore.shareProviderSubject.next({
        ...info,
        info: shareText,
      });
    },
  }),
);
